import { useCallback, useEffect, useRef, useState } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  SearchContextProvider,
  useSearch,
} from '@backstage/plugin-search-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { mcpChatAccessPermission } from '@internal/plugin-permissions-common';
import { SearchField } from '@backstage/ui';
import useDebounce from 'react-use/esm/useDebounce';

import styles from './SearchBar.module.css';

type SearchBarBaseProps = {
  /** The committed value; the input follows it when it changes from outside. */
  value?: string;
  /** Called with the input value once typing pauses for `debounceTime`. */
  onChange: (value: string) => void;
  /** Called with the input value on Enter. */
  onSubmit?: (value: string) => void;
  /** @defaultValue 200 */
  debounceTime?: number;
  /** A visible label; without one the field is labelled "Search". */
  label?: string;
  placeholder?: string;
  className?: string;
};

/**
 * The search input: a BUI search field whose value is debounced into
 * `onChange`. Enter submits, the clear button and Escape empty it.
 */
const SearchBarBase = ({
  onChange,
  onSubmit,
  debounceTime = 200,
  value: defaultValue = '',
  label,
  placeholder,
  className,
}: SearchBarBaseProps) => {
  const configApi = useApi(configApiRef);
  const enableAIChat =
    configApi.getOptionalBoolean('features.enable-ai-chat') ?? false;
  // Members of aiusers and admins, as the custom permission policy decides
  const { allowed: canUseXpert } = usePermission({
    permission: mcpChatAccessPermission,
  });
  const [value, setValue] = useState<string>('');
  const forwardedValueRef = useRef<string>('');

  useEffect(() => {
    setValue(prevValue => {
      // We only update the value if our current value is the same as it was
      // for the most recent onChange call. Otherwise it means that the users
      // has continued typing and we should not replace their input.
      if (prevValue === forwardedValueRef.current) {
        return String(defaultValue);
      }
      return prevValue;
    });
  }, [defaultValue, forwardedValueRef]);

  useDebounce(
    () => {
      forwardedValueRef.current = value;
      onChange(value);
    },
    debounceTime,
    [value],
  );

  const defaultPlaceholder =
    enableAIChat && canUseXpert
      ? 'Search or use xpert to get answers'
      : 'Search';

  return (
    <SearchContextProvider inheritParentContextIfAvailable>
      <SearchField
        name="search"
        size="medium"
        className={[styles.searchBar, className].filter(Boolean).join(' ')}
        label={label}
        aria-label={label ? undefined : 'Search'}
        placeholder={placeholder ?? defaultPlaceholder}
        value={value}
        onChange={setValue}
        onSubmit={submitted => onSubmit?.(submitted)}
        data-testid="search-bar-next"
      />
    </SearchContextProvider>
  );
};

/**
 * Props for {@link SearchBar}.
 *
 * @public
 */
export type SearchBarProps = Partial<SearchBarBaseProps>;

/**
 * The search bar bound to the search context: it shows the context's term
 * and, without an `onChange`, sets it.
 *
 * @public
 */
export const SearchBar = ({
  value: initialValue = '',
  onChange,
  ...rest
}: SearchBarProps) => {
  const { term, setTerm } = useSearch();

  useEffect(() => {
    if (initialValue) {
      setTerm(String(initialValue));
    }
  }, [initialValue, setTerm]);

  const handleChange = useCallback(
    (newValue: string) => {
      if (onChange) {
        onChange(newValue);
      } else {
        setTerm(newValue);
      }
    },
    [onChange, setTerm],
  );

  return <SearchBarBase {...rest} value={term} onChange={handleChange} />;
};
